// src/pages/ComingSoon.jsx
import React, { useState } from "react";
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  Input,
  HStack,
  VStack,
  useToast,
  Container,
  Icon,
  Badge,
  Link as ChakraLink,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { BsEnvelope, BsCheckCircle } from "react-icons/bs";

const MotionBox = motion(Box);

export default function ComingSoon({ message = null }) {
  const [email, setEmail] = useState("");
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  const handleNotify = async () => {
    if (!email) {
      toast({
        title: "Email required",
        description: "Please enter your email to get notified.",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
        status: "error",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 500));
    setIsLoading(false);

    setIsSubscribed(true);
    setEmail("");
    toast({
      title: "Success!",
      description: "You're on the waitlist. We'll notify you soon.",
      status: "success",
      duration: 2500,
      isClosable: true,
    });

    setTimeout(() => setIsSubscribed(false), 3000);
  };

  return (
    <>
      {/* Meta tags */}
      <meta property="og:title" content="Coming Soon | NoraHuman" />
      <meta
        property="og:description"
        content="Exciting new features coming to NoraHuman. Join the waitlist."
      />
      <meta
        name="description"
        content="Exciting new features coming to NoraHuman. Join the waitlist."
      />
      <link rel="canonical" href="https://norahuman.com/coming-soon" />

      <Box bg="white" minH="100vh" position="relative" overflow="hidden">
        {/* Subtle gradient background */}
        <Box
          position="absolute"
          inset={0}
          bgGradient="linear(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)"
          zIndex={0}
        />

        {/* Header */}
        <Box
          position="sticky"
          top={0}
          zIndex={100}
          bg="white"
          borderBottom="1px solid"
          borderColor="gray.100"
          backdropFilter="blur(10px)"
          bg="rgba(255, 255, 255, 0.8)"
        >
          <Container maxW="container.xl" py={4} px={{ base: 4, md: 6 }}>
            <HStack justify="space-between">
              <Text fontSize="lg" fontWeight="bold" color="teal.600">
                NoraHuman
              </Text>
              <HStack spacing={6} fontSize="sm">
                <ChakraLink
                  href="mailto:support@norahuman.com"
                  _hover={{ color: "teal.600" }}
                >
                  Contact
                </ChakraLink>
              </HStack>
            </HStack>
          </Container>
        </Box>

        {/* Main Content */}
        <Container
          maxW="container.md"
          py={{ base: 12, md: 20 }}
          position="relative"
          zIndex={1}
        >
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <VStack spacing={8} align="stretch">
              {/* Status Badge */}
              <VStack align="flex-start" spacing={2}>
                <Badge
                  colorScheme="teal"
                  variant="subtle"
                  px={3}
                  py={1}
                  fontSize="xs"
                  fontWeight="600"
                  textTransform="uppercase"
                  letterSpacing="0.5px"
                >
                  🚀 Private Beta
                </Badge>
                <Text fontSize="xs" color="gray.500" fontWeight="500">
                  Expected: Q1 2026
                </Text>
              </VStack>

              {/* Value Proposition - Main Headline */}
              <VStack align="flex-start" spacing={2}>
                <Heading
                  as="h1"
                  size="2xl"
                  lineHeight="1.2"
                  color="gray.900"
                  fontWeight="700"
                >
                  Workforce Intelligence
                  <br />
                  that actually works
                </Heading>
                <Text
                  fontSize="lg"
                  color="gray.600"
                  lineHeight="1.6"
                  fontWeight="500"
                >
                  {message ||
                    "Real-time HR insights and predictive analytics for teams that move fast. Make better decisions with data you can trust."}
                </Text>
              </VStack>

              {/* Visual Anchor - Minimal & Clean */}
              <Box
                bg="gray.50"
                borderRadius="lg"
                border="1px solid"
                borderColor="gray.200"
                p={{ base: 8, md: 12 }}
                textAlign="center"
                minH="280px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <VStack spacing={4}>
                  <Icon
                    as={() => (
                      <svg
                        width="64"
                        height="64"
                        viewBox="0 0 64 64"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect
                          x="8"
                          y="8"
                          width="48"
                          height="48"
                          rx="8"
                          fill="#e0f2fe"
                        />
                        <line
                          x1="16"
                          y1="24"
                          x2="48"
                          y2="24"
                          stroke="#0ea5e9"
                          strokeWidth="2"
                        />
                        <line
                          x1="16"
                          y1="32"
                          x2="48"
                          y2="32"
                          stroke="#0ea5e9"
                          strokeWidth="2"
                        />
                        <line
                          x1="16"
                          y1="40"
                          x2="32"
                          y2="40"
                          stroke="#0ea5e9"
                          strokeWidth="2"
                        />
                      </svg>
                    )}
                  />
                  <Text
                    fontSize="sm"
                    color="gray.600"
                    fontWeight="500"
                    maxW="200px"
                  >
                    Analytics dashboard with real-time data and actionable
                    insights
                  </Text>
                </VStack>
              </Box>

              {/* Email Capture - Prominent & Clear */}
              <VStack spacing={3} w="full">
                <Text
                  fontSize="sm"
                  fontWeight="600"
                  color="gray.700"
                  alignSelf="flex-start"
                >
                  Join the waitlist
                </Text>

                {!isSubscribed ? (
                  <HStack w="full" spacing={2}>
                    <Input
                      placeholder="you@company.com"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && handleNotify()}
                      rounded="lg"
                      px={4}
                      py={3}
                      borderWidth="1px"
                      borderColor="gray.300"
                      bg="white"
                      fontSize="sm"
                      _focus={{
                        borderColor: "teal.400",
                        boxShadow: "0 0 0 3px rgba(20, 184, 166, 0.1)",
                        outline: "none",
                      }}
                      _placeholder={{ color: "gray.400" }}
                      flex={1}
                    />
                    <Button
                      colorScheme="teal"
                      size="md"
                      px={6}
                      rounded="lg"
                      fontWeight="600"
                      onClick={handleNotify}
                      isLoading={isLoading}
                      loadingText="Subscribing..."
                      _hover={{
                        transform: "translateY(-1px)",
                        shadow: "md",
                      }}
                      transition="all 0.2s"
                    >
                      Get notified
                    </Button>
                  </HStack>
                ) : (
                  <motion.div
                    key="success"
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.3 }}
                    style={{ width: "100%" }}
                  >
                    <HStack
                      w="full"
                      p={3}
                      bg="green.50"
                      rounded="lg"
                      borderWidth="1px"
                      borderColor="green.200"
                      spacing={3}
                    >
                      <Icon
                        as={BsCheckCircle}
                        color="green.600"
                        boxSize={5}
                        flexShrink={0}
                      />
                      <VStack align="flex-start" spacing={0}>
                        <Text color="green.700" fontSize="sm" fontWeight="600">
                          Confirmed!
                        </Text>
                        <Text color="green.600" fontSize="xs">
                          Check your inbox for updates
                        </Text>
                      </VStack>
                    </HStack>
                  </motion.div>
                )}

                <Text fontSize="xs" color="gray.500">
                  No spam, ever. Unsubscribe anytime.
                </Text>
              </VStack>

              {/* Social Proof Placeholder */}
              <VStack
                spacing={3}
                w="full"
                pt={4}
                borderTop="1px solid"
                borderColor="gray.200"
              >
                <Text
                  fontSize="xs"
                  color="gray.500"
                  fontWeight="600"
                  textTransform="uppercase"
                >
                  Trusted by forward-thinking teams
                </Text>
                <HStack spacing={6} justify="center" wrap="wrap">
                  {["Acme Corp", "TechFlow", "DataDriven Co"].map(
                    (company, idx) => (
                      <Text
                        key={idx}
                        fontSize="xs"
                        color="gray.400"
                        fontWeight="500"
                      >
                        {company}
                      </Text>
                    )
                  )}
                </HStack>
              </VStack>

              {/* Launch Indicator */}
              <VStack
                spacing={2}
                bg="gray.50"
                p={4}
                rounded="lg"
                w="full"
                borderWidth="1px"
                borderColor="gray.200"
              >
                <Text fontSize="xs" color="gray.600" fontWeight="600">
                  What's coming
                </Text>
                <VStack align="flex-start" spacing={2} w="full">
                  {[
                    "Real-time workforce analytics",
                    "Predictive attrition insights",
                    "Compliance automation",
                  ].map((feature, idx) => (
                    <HStack key={idx} spacing={2}>
                      <Box
                        w={1.5}
                        h={1.5}
                        bg="teal.400"
                        borderRadius="full"
                        flexShrink={0}
                      />
                      <Text fontSize="sm" color="gray.700">
                        {feature}
                      </Text>
                    </HStack>
                  ))}
                </VStack>
              </VStack>
            </VStack>
          </MotionBox>
        </Container>

        {/* Footer - Minimal Navigation */}
        <Box bg="gray.50" borderTop="1px solid" borderColor="gray.200" mt={12}>
          <Container maxW="container.xl" py={6} px={{ base: 4, md: 6 }}>
            <HStack justify="space-between" fontSize="xs" color="gray.600">
              <Text>© 2025 NoraHuman. All rights reserved.</Text>
              <HStack spacing={4}>
                <ChakraLink href="#" _hover={{ color: "gray.900" }}>
                  Privacy Policy
                </ChakraLink>
                <ChakraLink
                  href="mailto:support@norahuman.com"
                  _hover={{ color: "gray.900" }}
                >
                  Support
                </ChakraLink>
              </HStack>
            </HStack>
          </Container>
        </Box>
      </Box>
    </>
  );
}
